import {useCallback, useEffect, useRef, useState} from 'react';
import {RemoteParticipant} from 'livekit-client';
import {
  useRoomContext,
  useVoiceAssistant,
  TrackReferenceOrPlaceholder
} from '@livekit/components-react';

export interface IUseLiveKitAvatarReturn {
  isAvatarSyncing: boolean;
  isAvatarEnabled: boolean;
  avatarCamTrackRef?: TrackReferenceOrPlaceholder;
  toggleAvatar: () => Promise<void>;
}

export const useLiveKitAvatar = (): IUseLiveKitAvatarReturn => {
  const {videoTrack: avatarCamTrackRef} = useVoiceAssistant();
  const room = useRoomContext();

  const isAvatarOnFeRef = useRef(false);
  const isAvatarOnBeRef = useRef(false);

  const [isAvatarEnabled, setIsAvatarEnabled] = useState(false);
  const [isAvatarSyncing, setIsAvatarSyncing] = useState(false);

  const interval = useRef<NodeJS.Timeout | undefined>(undefined);

  // ParticipantKind: STANDARD=0, INGRESS=1, EGRESS=2, SIP=3, AGENT=4
  const getAgentParticipant = useCallback((): RemoteParticipant | undefined => {
    for (const [, p] of room.remoteParticipants) {
      if (p.kind === 4) {
        return p;
      }
    }
    return undefined;
  }, [room.remoteParticipants]);

  useEffect(() => {
    const sync = () => {
      const agent = getAgentParticipant();
      if (!agent) return;
      isAvatarOnBeRef.current = agent.attributes?.['avatar.enabled'] === 'true';
      setIsAvatarSyncing(isAvatarOnFeRef.current !== isAvatarOnBeRef.current);
      console.info(`[Avatar]: fe: ${isAvatarOnFeRef.current}, be: ${isAvatarOnBeRef.current}.`);
    };

    interval.current = setInterval(sync, 1000);
  }, [getAgentParticipant]);

  const callAgentRpc = useCallback(
    async (method: string, payload = '') => {
      const agent = getAgentParticipant();
      if (!agent) {
        console.warn('Agent not found in room');
        return null;
      }
      try {
        console.info(`[Avatar] Calling RPC ${method} with payload:`, payload);
        const response = await room.localParticipant.performRpc({
          destinationIdentity: agent.identity,
          method,
          payload,
          responseTimeout: 10_000
        });
        console.info(`[Avatar] Calling RPC ${method} result:`, response);
        return JSON.parse(response);
      } catch (err) {
        console.error(`RPC ${method} failed:`, err);
        return null;
      }
    },
    [getAgentParticipant, room.localParticipant]
  );

  const toggleAvatar = useCallback(async () => {
    const result = await callAgentRpc('toggle_avatar');
    // RPC returns {"enabled": true|false} — use it to update state
    // immediately, without waiting for the attribute event/poll. Makes
    // the toggle feel instant.
    if (result && typeof result.enabled === 'boolean') {
      setIsAvatarSyncing(true);
      setIsAvatarEnabled((prev) => (prev === result.enabled ? prev : result.enabled));
      isAvatarOnFeRef.current = result.enabled;
    }
    return result;
  }, [callAgentRpc]);

  return {
    isAvatarSyncing,
    isAvatarEnabled,
    avatarCamTrackRef,
    toggleAvatar
  };
};
